import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Input, Button, Row, Col, Form, FormInstance } from "antd";
import { Rule } from "antd/es/form";
import { PasswordComplexity } from "utils/checkPasswordComplexity";
import { FormCard } from "Components/Form/FormCard";
import { securityStore } from "Pages/UserSettings/Stores/SecurityStore";
import { I18N_NS } from "Config/i18n";
import { loginSettingsData } from "Stores";
import { Loader } from "Components/General/Loader";
import { AdditionalPasswordRequirements } from "utils/AdditionalPasswordRequirements";

interface FormValues {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

export const UserSecurity: React.FC = observer(() => {
  const { t } = useTranslation(I18N_NS.USER_SETTINGS);
  const [confirmDirty, setConfirmDirty] = useState(false);
  const [password, setPassword] = useState("");
  const formRef = useRef<FormInstance>(null);

  useEffect(() => {
    (async () => {
      await loginSettingsData.passwordDefinitions();
    })();

    return () => {
      securityStore.resetErrorMessage();
      loginSettingsData.clearPasswordPolicy();
    };
  }, []);

  const handleSubmit = useCallback((values: FormValues) => {
    securityStore.submit(values);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    if (!newPassword.length) {
      securityStore.resetErrorMessage();
    }

    formRef.current?.validateFields();
  }, []);

  const checkPassword = useCallback(
    async (_rule: Rule, value: string) => {
      try {
        if (value && value !== formRef.current?.getFieldValue("newPassword")) {
          return Promise.reject(t("Passwords do not match"));
        } else {
          return Promise.resolve();
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [t]
  );

  const checkConfirm = useCallback(
    async (_rule: Rule, value: string) => {
      try {
        if (value && confirmDirty) {
          return Promise.resolve();
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [confirmDirty]
  );

  const handleConfirmBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmDirty((prev) => prev || !!value);
  }, []);

  return (
    <Row gutter={20}>
      <Col span={12}>
        <FormCard title={t("Password")}>
          <Form requiredMark={false} ref={formRef} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label={t("Old password")}
              name="oldPassword"
              rules={[{ required: true, message: t("Please enter old password") }]}
            >
              <Input.Password type="password" placeholder={t("Old password")} autoFocus={true} />
            </Form.Item>
            <Form.Item
              label={t("New password")}
              name="newPassword"
              rules={[
                { required: true, message: t("Please enter new password") },
                { validator: checkConfirm },
                { validateTrigger: ["onChange", "onBlur"] },
              ]}
            >
              <Input.Password
                type="password"
                onBlur={handleConfirmBlur}
                placeholder={t("New password")}
                onChange={handleInputChange}
              />
            </Form.Item>
            <Form.Item
              label={t("Confirm new password")}
              name="newPasswordConfirmation"
              rules={[{ required: true, message: t("Please confirm new password") }, { validator: checkPassword }]}
            >
              <Input.Password type="password" placeholder={t("Confirm new password")} />
            </Form.Item>

            <Form.Item className="text-right">
              <Button type="primary" htmlType="submit">
                {t("Change password")}
              </Button>
            </Form.Item>
          </Form>
        </FormCard>
      </Col>
      <Col span={12}>
        {loginSettingsData.passwordPolicy ? (
          <>
            <PasswordComplexity inputValue={password} />
            <AdditionalPasswordRequirements errorMessage={securityStore.errorMessage} />
          </>
        ) : (
          <Loader />
        )}
      </Col>
    </Row>
  );
});
